<script>
    import { onMount, onDestroy } from "svelte";
    import { loadScript } from "$lib/utils/scripts";
    import SoundToggle from "$lib/components/SoundToggle.svelte";

    const SOUND_KEY = "dinglandia:sound-enabled";
    const FPS_DEBUG_KEY = "dinglandia:warrior-debug-fps";

    let canvasEl;
    let containerEl;
    let glCanvasEl;
    let destroyGame = () => {};
    let webglBackground = null;

    let score = $state(0);
    let lives = $state(3);
    let gameState = $state("title");
    let showGameOver = $state(false);
    let showFpsMeter = $state(false);
    let fpsStats = $state({ fps: 0, avgMs: 0, worstMs: 0 });

    // ── Leaderboard ───────────────────────────────────────────
    const SCORES_KEY = "dinglandia:warrior-scores";
    const MAX_SCORES = 10;
    const SCORES_API =
        "https://warrior-leaderboard.dinglestein.workers.dev/api/warrior-scores";

    let scores = $state([]);
    let titleScores = $state([]);
    let leaderPhase = $state("entry"); // "entry" | "board"
    let playerName = $state("");
    let qualifies = $state(false);
    let newEntryIdx = $state(-1);
    let completedGame = $state(false);
    let scoresLoading = $state(false);

    async function fetchScores() {
        try {
            const res = await fetch(SCORES_API);
            if (!res.ok) throw new Error("api");
            return await res.json();
        } catch {
            // Fallback to localStorage if offline or API unreachable
            try {
                const raw = localStorage.getItem(SCORES_KEY);
                return raw ? JSON.parse(raw) : [];
            } catch {
                return [];
            }
        }
    }

    function qualifiesForBoard(s, globalScores) {
        return (
            globalScores.length < MAX_SCORES ||
            s > globalScores[globalScores.length - 1].score
        );
    }

    async function postScore(name, s, completed = false) {
        const cleanName = name.trim() || "Unknown Warrior";
        try {
            await fetch(SCORES_API, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: cleanName,
                    score: s,
                    completed,
                    date: Date.now(),
                }),
            });
        } catch {}
        return fetchScores();
    }

    async function openGameOver() {
        scoresLoading = true;
        showGameOver = true;
        playerName = "";
        newEntryIdx = -1;
        const global = await fetchScores();
        scores = global;
        qualifies = qualifiesForBoard(score, global);
        leaderPhase = qualifies ? "entry" : "board";
        scoresLoading = false;
    }

    async function handleNameSubmit(e) {
        e.preventDefault();
        scoresLoading = true;
        leaderPhase = "board";
        const saved = await postScore(playerName, score, completedGame);
        scores = saved;
        titleScores = saved;
        const cleanName = playerName.trim() || "Unknown Warrior";
        newEntryIdx = saved.findIndex(
            (en) => en.name === cleanName && en.score === score,
        );
        scoresLoading = false;
    }

    async function handleSkipEntry() {
        scoresLoading = true;
        leaderPhase = "board";
        const saved = await postScore("Unknown Warrior", score, completedGame);
        scores = saved;
        titleScores = saved;
        newEntryIdx = saved.findIndex(
            (en) => en.name === "Unknown Warrior" && en.score === score,
        );
        scoresLoading = false;
    }

    const SECTION_TRACKS = [
        "/sounds/music/song-of-midgard-forest.mp3", // 0 Midgard Forest
        "/sounds/music/bifrost-bridge.mp3", // 1 Bifrost Bridge
        "/sounds/music/echoes-of-jotunheim.mp3", // 2 Jotunheim
        "/sounds/music/niflheim-of-fog.mp3", // 3 Niflheim
        "/sounds/music/tale-of-muspelheim.mp3", // 4 Muspelheim
        "/sounds/music/hymn-of-alfheim.mp3", // 5 Alfheim
        "/sounds/music/svartalfheims-forge.mp3", // 6 Svartalfheim
        "/sounds/music/helheims-harp.mp3", // 7 Helheim
        "/sounds/music/the-gates-of-valhalla.mp3", // 8 Gates of Valhalla
        "/sounds/retro-ragnarok.mp3", // 9 Menu / intro / death screen
    ];
    const MENU_IDX = 9;
    const CROSSFADE_MS = 2000;
    const FADEOUT_MS = 1000;
    const WARRIOR_GAME_SCRIPT_SRC =
        "/scripts/warrior-game.js?v=warrior-2026-03-14-skranji-restore";

    const musicCache = new Map();
    let activeIdx = -1;
    let musicRafId = null;
    let soundObserver = null;
    let fpsRafId = null;
    let layoutSyncRafIds = [];
    let gameplayResizeObserver = null;
    let onViewportResize = null;

    function shouldShowWebGLBackground() {
        return gameState === "title" || showGameOver || gameState === "victory";
    }

    function isSoundEnabled() {
        try {
            return localStorage.getItem(SOUND_KEY) === "true";
        } catch {
            return false;
        }
    }

    function readFpsDebugPreference() {
        if (typeof window === "undefined") return false;
        const debugParam = new URLSearchParams(window.location.search).get(
            "debugFps",
        );
        if (debugParam === "1" || debugParam === "true") return true;
        if (debugParam === "0" || debugParam === "false") return false;
        try {
            return localStorage.getItem(FPS_DEBUG_KEY) === "true";
        } catch {
            return false;
        }
    }

    function setFpsMeterEnabled(enabled, persist = true) {
        showFpsMeter = enabled;
        if (!persist) return;
        const persistPreference = () => {
            try {
                localStorage.setItem(FPS_DEBUG_KEY, String(enabled));
            } catch {}
        };
        if (typeof window !== "undefined" && "requestIdleCallback" in window) {
            window.requestIdleCallback(persistPreference, { timeout: 500 });
        } else {
            setTimeout(persistPreference, 0);
        }
    }

    function stopFpsMeter() {
        if (fpsRafId) {
            cancelAnimationFrame(fpsRafId);
            fpsRafId = null;
        }
    }

    function cancelLayoutSync() {
        layoutSyncRafIds.forEach((id) => cancelAnimationFrame(id));
        layoutSyncRafIds = [];
    }

    function syncGameplayLayout(passes = 3) {
        if (typeof window === "undefined") return;
        cancelLayoutSync();
        let remaining = Math.max(1, passes | 0);

        const tick = () => {
            window.__warriorGameResize?.(2);
            remaining -= 1;
            if (remaining > 0) {
                layoutSyncRafIds.push(requestAnimationFrame(tick));
            }
        };

        layoutSyncRafIds.push(requestAnimationFrame(tick));
    }

    function startFpsMeter() {
        if (fpsRafId || typeof window === "undefined") return;

        let lastTs = 0;
        let windowStart = performance.now();
        let frames = 0;
        let worstMs = 0;

        function tick(now) {
            if (lastTs) {
                worstMs = Math.max(worstMs, now - lastTs);
            }
            lastTs = now;
            frames += 1;

            const elapsed = now - windowStart;
            if (elapsed >= 500) {
                fpsStats = {
                    fps: (frames * 1000) / elapsed,
                    avgMs: elapsed / Math.max(frames, 1),
                    worstMs,
                };
                windowStart = now;
                frames = 0;
                worstMs = 0;
            }

            fpsRafId = requestAnimationFrame(tick);
        }

        fpsRafId = requestAnimationFrame(tick);
    }

    function getAudio(idx) {
        if (!musicCache.has(idx)) {
            const a = new Audio(SECTION_TRACKS[idx]);
            a.loop = true;
            a.volume = 0;
            musicCache.set(idx, a);
        }
        return musicCache.get(idx);
    }

    function prefetchAudio(idx) {
        if (idx >= 0 && idx < SECTION_TRACKS.length && !musicCache.has(idx))
            getAudio(idx);
    }

    function cancelMusicFade() {
        if (musicRafId) {
            cancelAnimationFrame(musicRafId);
            musicRafId = null;
        }
    }

    function crossfadeTo(idx) {
        if (idx === activeIdx) return;
        const soundOn = isSoundEnabled();
        const outAudio =
            activeIdx >= 0 ? (musicCache.get(activeIdx) ?? null) : null;
        const inAudio = getAudio(idx);
        activeIdx = idx;
        cancelMusicFade();

        if (!soundOn) {
            if (outAudio) {
                outAudio.pause();
                outAudio.volume = 0;
            }
            return;
        }

        if (inAudio.currentTime < 6) inAudio.currentTime = 6;
        inAudio.play().catch(() => {});
        const t0 = performance.now();
        const outStart = outAudio?.volume ?? 0;

        function tick(now) {
            const t = Math.min((now - t0) / CROSSFADE_MS, 1);
            if (outAudio) outAudio.volume = outStart * (1 - t);
            inAudio.volume = t;
            if (t < 1) {
                musicRafId = requestAnimationFrame(tick);
            } else {
                if (outAudio) {
                    outAudio.pause();
                    outAudio.volume = 0;
                }
                inAudio.volume = 1;
                musicRafId = null;
            }
        }
        musicRafId = requestAnimationFrame(tick);
    }

    function fadeOutAll() {
        cancelMusicFade();
        const active = [...musicCache.values()].filter((a) => !a.paused);
        if (!active.length) {
            activeIdx = -1;
            return;
        }
        const t0 = performance.now();
        const snaps = active.map((a) => [a, a.volume]);
        function tick(now) {
            const t = Math.min((now - t0) / FADEOUT_MS, 1);
            for (const [a, sv] of snaps) a.volume = sv * (1 - t);
            if (t < 1) {
                musicRafId = requestAnimationFrame(tick);
            } else {
                for (const [a] of snaps) {
                    a.pause();
                    a.volume = 0;
                }
                activeIdx = -1;
                musicRafId = null;
            }
        }
        musicRafId = requestAnimationFrame(tick);
    }

    function syncMusic(state) {
        if (state === "gameover" || state === "victory") {
            crossfadeTo(MENU_IDX);
            return;
        }
        // title / intro / playing
        const soundOn = isSoundEnabled();
        if (activeIdx < 0) {
            if (soundOn) crossfadeTo(MENU_IDX);
            return;
        }
        const a = musicCache.get(activeIdx);
        if (!a) return;
        if (soundOn) {
            if (a.volume <= 0.001) {
                a.volume = 1;
            }
            a.play().catch(() => {});
        } else {
            a.pause();
        }
    }

    /* ── WebGL Background ─────────────────────────────────── */
    function initWebGLBackground(canvas) {
        const noopBackground = {
            setActive() {},
            destroy() {},
        };
        const gl = canvas.getContext("webgl", {
            alpha: true,
            antialias: false,
            premultipliedAlpha: true,
        });
        if (!gl) {
            return noopBackground;
        }
        const prefersReducedMotion =
            typeof window !== "undefined" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        const VERT = `
            attribute vec2 aPos;
            void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
        `;

        const FRAG = `
            precision mediump float;
            uniform float uTime;
            uniform vec2 uRes;
            uniform float uMotion;

            float hash(vec2 p) {
                return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
            }

            float noise(vec2 p) {
                vec2 i = floor(p);
                vec2 f = fract(p);
                f = f * f * (3.0 - 2.0 * f);
                float a = hash(i);
                float b = hash(i + vec2(1.0, 0.0));
                float c = hash(i + vec2(0.0, 1.0));
                float d = hash(i + vec2(1.0, 1.0));
                return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
            }

            float fbm(vec2 p) {
                float v = 0.0;
                float a = 0.5;
                for (int i = 0; i < 4; i++) {
                    v += a * noise(p);
                    p *= 2.1;
                    a *= 0.5;
                }
                return v;
            }

            vec2 pixelate(vec2 uv, float size) {
                return floor(uv * size) / size;
            }

            float segmentDistance(vec2 p, vec2 a, vec2 b) {
                vec2 pa = p - a;
                vec2 ba = b - a;
                float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
                return length(pa - ba * h);
            }

            float segmentGlow(vec2 p, vec2 a, vec2 b, float thickness) {
                return smoothstep(thickness, thickness * 0.2, segmentDistance(p, a, b));
            }

            float boxMask(vec2 p, vec2 center, vec2 halfSize, float feather) {
                vec2 d = abs(p - center) - halfSize;
                return 1.0 - smoothstep(0.0, feather, max(d.x, d.y));
            }

            float circleMask(vec2 p, vec2 center, float radius, float feather) {
                return smoothstep(radius + feather, radius - feather, length(p - center));
            }

            float ringMask(vec2 p, vec2 center, float radius, float width) {
                float d = abs(length(p - center) - radius);
                return smoothstep(width, width * 0.2, d);
            }

            float triangleSail(vec2 p, vec2 origin, vec2 size) {
                vec2 local = p - origin;
                if (local.x < 0.0 || local.x > size.x || local.y < 0.0 || local.y > size.y) return 0.0;
                float edge = size.y * (1.0 - local.x / size.x);
                return step(local.y, edge);
            }

            void main() {
                vec2 uv = gl_FragCoord.xy / uRes;
                vec2 pxUv = pixelate(uv, 170.0);
                float t = uTime * mix(0.08, 0.42, uMotion);
                float horizon = 0.34;
                vec3 col = mix(vec3(0.06, 0.05, 0.14), vec3(0.95, 0.42, 0.18), pow(max(pxUv.y, 0.0), 1.45));

                float stormBands = fbm(vec2(pxUv.x * 3.2 - t * 0.45, pxUv.y * 4.6 + t * 0.1));
                float emberBands = fbm(vec2(pxUv.x * 5.8 + t * 0.28, pxUv.y * 3.0 - t * 0.05));
                col = mix(col, vec3(0.14, 0.08, 0.24), smoothstep(0.2, 0.78, stormBands) * (0.8 + pxUv.y * 0.4));
                col += vec3(0.45, 0.14, 0.06) * smoothstep(0.56, 0.92, emberBands) * smoothstep(0.36, 1.0, pxUv.y);

                float stormFocusX = 0.34;
                vec2 sunCenter = vec2(stormFocusX, 0.68);
                float sunDist = length((pxUv - sunCenter) * vec2(1.0, 1.2));
                float corona = ringMask(pxUv, sunCenter, 0.18, 0.028);
                float coronaFire = fbm(vec2(atan(pxUv.y - sunCenter.y, pxUv.x - sunCenter.x) * 1.6 + t * 0.8, sunDist * 14.0 - t * 0.4));
                col += vec3(1.0, 0.44, 0.12) * corona * (0.8 + coronaFire * 1.2);
                col += vec3(0.95, 0.36, 0.1) * smoothstep(0.35, 0.0, abs(sunDist - 0.24)) * 0.4;
                col = mix(col, vec3(0.03, 0.03, 0.07), smoothstep(0.19, 0.14, sunDist));

                float runeGlow = 0.0;
                for (int i = 0; i < 3; i++) {
                    float fi = float(i);
                    vec2 rp = vec2(0.76, 0.56 + fi * 0.08);
                    float flicker = 0.72 + 0.28 * sin(t * 8.0 + fi * 2.0);
                    float diamond = smoothstep(0.024, 0.012, abs(abs(pxUv.x - rp.x) + abs(pxUv.y - rp.y) - 0.022));
                    float stem = boxMask(pxUv, rp + vec2(0.0, 0.028), vec2(0.004, 0.024), 0.002);
                    runeGlow += (diamond + stem) * flicker;
                }
                col += vec3(1.0, 0.58, 0.16) * runeGlow * 0.65;

                float flash = pow(max(0.0, sin(t * 6.5) * 0.5 + 0.5), 8.0);
                vec2 bolt0 = vec2(stormFocusX, 0.72);
                vec2 bolt1 = vec2(stormFocusX + 0.02 + sin(t * 1.3) * 0.03, 0.57);
                vec2 bolt2 = vec2(stormFocusX - 0.02 + sin(t * 1.9 + 1.2) * 0.04, 0.42);
                vec2 bolt3 = vec2(stormFocusX + 0.02 + sin(t * 2.5 + 2.0) * 0.03, 0.25);
                vec2 bolt4 = vec2(stormFocusX - 0.01, 0.06);
                float bolt = 0.0;
                bolt += segmentGlow(pxUv, bolt0, bolt1, 0.018);
                bolt += segmentGlow(pxUv, bolt1, bolt2, 0.016);
                bolt += segmentGlow(pxUv, bolt2, bolt3, 0.014);
                bolt += segmentGlow(pxUv, bolt3, bolt4, 0.012);
                bolt += segmentGlow(pxUv, bolt1, bolt1 + vec2(-0.12, -0.1), 0.01) * 0.75;
                bolt += segmentGlow(pxUv, bolt2, bolt2 + vec2(0.11, -0.08), 0.01) * 0.75;
                bolt += segmentGlow(pxUv, bolt2 + vec2(0.02, -0.02), bolt2 + vec2(-0.09, -0.12), 0.008) * 0.65;
                vec3 lightning = mix(vec3(0.5, 0.72, 1.0), vec3(1.0), 0.45 + flash * 0.35);
                col += lightning * bolt * (0.7 + flash * 1.6);
                col += vec3(0.35, 0.6, 1.0) * smoothstep(0.18, 0.0, distance(pxUv, vec2(stormFocusX, 0.1))) * flash * 0.35;

                float forestLine = horizon + 0.035 + noise(vec2(pxUv.x * 11.0, 3.0)) * 0.03;
                if (pxUv.y < forestLine && pxUv.y > horizon - 0.02) {
                    col = mix(col, vec3(0.11, 0.06, 0.16), 0.9);
                    float treeNoise = fract(pxUv.x * 52.0);
                    if (treeNoise < 0.14 && pxUv.y < forestLine + treeNoise * 0.025) {
                        col = vec3(0.1, 0.05, 0.14);
                    }
                }

                if (pxUv.y < horizon) {
                    float sea = pxUv.y / horizon;
                    col = mix(vec3(0.03, 0.08, 0.18), vec3(0.13, 0.42, 0.67), sea);
                    float wave1 = sin(pxUv.x * 24.0 + t * 4.0 + sea * 6.0);
                    float wave2 = sin(pxUv.x * 39.0 - t * 3.2 + sea * 11.0);
                    float waveMix = wave1 * 0.55 + wave2 * 0.35;
                    col += vec3(0.02, 0.07, 0.11) * waveMix;
                    float foam = smoothstep(0.58, 0.82, fbm(vec2(pxUv.x * 12.0 - t * 1.6, pxUv.y * 22.0 + t * 0.5)) + waveMix * 0.28);
                    col += vec3(0.7, 0.84, 0.92) * foam * smoothstep(0.0, 0.9, sea) * 0.65;
                    col += vec3(0.35, 0.62, 0.84) * smoothstep(0.0, 0.12, abs(pxUv.y - horizon + 0.01));
                }

                for (int shipIndex = 0; shipIndex < 2; shipIndex++) {
                    float fi = float(shipIndex);
                    float depth = 1.0 - fi * 0.24;
                    vec2 shipPos = vec2(0.2 + fi * 0.22, horizon - 0.055 - fi * 0.022);
                    shipPos.x += sin(t * (1.8 + fi * 0.2)) * 0.015 * depth;
                    shipPos.y += sin(t * (3.2 + fi) + fi) * 0.008 * depth;
                    vec2 shipUv = (pxUv - shipPos) / (0.12 * depth);

                    float hull = boxMask(shipUv, vec2(0.0, -0.08), vec2(0.42, 0.09), 0.015);
                    hull *= smoothstep(0.64, 0.08, abs(shipUv.x) + max(shipUv.y, 0.0) * 0.5);
                    float sailA = triangleSail(shipUv, vec2(-0.05, 0.02), vec2(0.32, 0.42));
                    float mast = boxMask(shipUv, vec2(-0.02, 0.1), vec2(0.02, 0.38), 0.01);
                    float stripe = step(0.45, fract((shipUv.x + shipUv.y * 0.3) * 8.0));
                    float oar = boxMask(shipUv, vec2(-0.38, -0.03), vec2(0.06, 0.01), 0.006) + boxMask(shipUv, vec2(0.38, -0.03), vec2(0.06, 0.01), 0.006);
                    col = mix(col, vec3(0.34, 0.18, 0.09), hull * 0.95);
                    col = mix(col, mix(vec3(0.95, 0.92, 0.8), vec3(0.82, 0.16, 0.12), stripe), sailA * 0.95);
                    col = mix(col, vec3(0.24, 0.14, 0.08), mast * 0.95);
                    col = mix(col, vec3(0.52, 0.3, 0.12), oar * 0.85);
                    float wake = smoothstep(0.16, 0.0, distance(pxUv, shipPos + vec2(0.0, -0.075)));
                    col += vec3(0.7, 0.9, 0.98) * wake * 0.18 * depth;
                }

                float islandLeftMask = smoothstep(0.54, 0.64, pxUv.x);
                float islandRightMask = 1.0 - smoothstep(0.98, 1.04, pxUv.x);
                float islandTop = 0.11;
                islandTop += smoothstep(0.54, 0.72, pxUv.x) * 0.07;
                islandTop += smoothstep(0.72, 0.9, pxUv.x) * 0.11;
                islandTop += noise(vec2(pxUv.x * 14.0, 9.0)) * 0.03;
                float islandMask = islandLeftMask * islandRightMask * step(pxUv.y, islandTop);
                if (islandMask > 0.0) {
                    float rockTex = fbm(vec2(pxUv.x * 12.0, pxUv.y * 18.0));
                    vec3 islandCol = mix(vec3(0.18, 0.12, 0.08), vec3(0.42, 0.26, 0.16), rockTex);
                    if (pxUv.y > islandTop - 0.04) {
                        islandCol = mix(islandCol, vec3(0.48, 0.7, 0.2), 0.85);
                    }
                    col = mix(col, islandCol, islandMask);
                }

                for (int stoneIndex = 0; stoneIndex < 3; stoneIndex++) {
                    float fi = float(stoneIndex);
                    vec2 stonePos = vec2(0.67 + fi * 0.04, 0.09 + fi * 0.005);
                    vec2 stoneUv = pxUv - stonePos;
                    float stone = boxMask(pxUv, stonePos, vec2(0.014 + fi * 0.002, 0.038 + fi * 0.004), 0.005);
                    float stoneTop = circleMask(pxUv, stonePos + vec2(0.0, 0.028 + fi * 0.004), 0.016 + fi * 0.003, 0.004);
                    float runeCut = boxMask(pxUv, stonePos + vec2(0.0, 0.008), vec2(0.002, 0.018), 0.002);
                    runeCut += boxMask(pxUv, stonePos + vec2(0.0, 0.008), vec2(0.008, 0.002), 0.002);
                    float stoneMask = min(1.0, stone + stoneTop);
                    col = mix(col, vec3(0.58, 0.53, 0.46), stoneMask * 0.92);
                    col = mix(col, vec3(0.18, 0.14, 0.11), runeCut * stoneMask);
                }

                float legSwing = 0.0;
                vec2 heroAnchor = vec2(0.812, 0.236);

                float leftLeg = boxMask(pxUv, heroAnchor + vec2(-0.017, -0.012 + legSwing), vec2(0.014, 0.055), 0.007);
                float rightLeg = boxMask(pxUv, heroAnchor + vec2(0.016, -0.012 - legSwing), vec2(0.014, 0.053), 0.007);
                float leftBootFur = boxMask(pxUv, heroAnchor + vec2(-0.019, -0.053 + legSwing), vec2(0.021, 0.008), 0.004);
                float rightBootFur = boxMask(pxUv, heroAnchor + vec2(0.019, -0.051 - legSwing), vec2(0.021, 0.008), 0.004);
                float leftBoot = boxMask(pxUv, heroAnchor + vec2(-0.021, -0.068 + legSwing), vec2(0.024, 0.012), 0.005);
                float rightBoot = boxMask(pxUv, heroAnchor + vec2(0.022, -0.066 - legSwing), vec2(0.024, 0.012), 0.005);

                float torsoBase = boxMask(pxUv, heroAnchor + vec2(0.0, 0.064), vec2(0.04, 0.06), 0.008);
                float vest = boxMask(pxUv, heroAnchor + vec2(0.0, 0.062), vec2(0.031, 0.05), 0.006);
                float furTrim = boxMask(pxUv, heroAnchor + vec2(0.0, 0.115), vec2(0.035, 0.008), 0.004);
                float belt = boxMask(pxUv, heroAnchor + vec2(0.0, 0.005), vec2(0.041, 0.01), 0.004);
                float buckle = boxMask(pxUv, heroAnchor + vec2(0.0, 0.005), vec2(0.012, 0.01), 0.003);

                vec2 shieldCenter = heroAnchor + vec2(0.056, 0.052);
                float shieldRim = ringMask(pxUv, shieldCenter, 0.047, 0.016);
                float shieldFace = circleMask(pxUv, shieldCenter, 0.036, 0.008);
                float shieldCross = boxMask(pxUv, shieldCenter, vec2(0.005, 0.026), 0.003)
                    + boxMask(pxUv, shieldCenter, vec2(0.026, 0.005), 0.003);
                float shieldBossOuter = circleMask(pxUv, shieldCenter, 0.013, 0.004);
                float shieldBossInner = circleMask(pxUv, shieldCenter, 0.006, 0.003);
                float shieldStrap = segmentGlow(pxUv, heroAnchor + vec2(0.03, 0.09), shieldCenter + vec2(-0.006, -0.004), 0.012);

                float head = boxMask(pxUv, heroAnchor + vec2(0.0, 0.15), vec2(0.03, 0.03), 0.006);
                float faceShadeL = boxMask(pxUv, heroAnchor + vec2(-0.024, 0.15), vec2(0.006, 0.028), 0.003);
                float faceShadeR = boxMask(pxUv, heroAnchor + vec2(0.024, 0.15), vec2(0.006, 0.028), 0.003);
                float cheek = boxMask(pxUv, heroAnchor + vec2(0.0, 0.135), vec2(0.014, 0.008), 0.003);
                float brow = boxMask(pxUv, heroAnchor + vec2(0.0, 0.161), vec2(0.019, 0.004), 0.002);
                float eyeA = boxMask(pxUv, heroAnchor + vec2(-0.011, 0.152), vec2(0.0045, 0.0035), 0.0014);
                float eyeB = boxMask(pxUv, heroAnchor + vec2(0.011, 0.152), vec2(0.0045, 0.0035), 0.0014);
                float eyeCatchA = boxMask(pxUv, heroAnchor + vec2(-0.013, 0.153), vec2(0.0015, 0.0015), 0.0008);
                float eyeCatchB = boxMask(pxUv, heroAnchor + vec2(0.009, 0.153), vec2(0.0015, 0.0015), 0.0008);
                float nose = boxMask(pxUv, heroAnchor + vec2(0.002, 0.145), vec2(0.003, 0.008), 0.002);

                float beardMain = boxMask(pxUv, heroAnchor + vec2(0.0, 0.11), vec2(0.022, 0.024), 0.006);
                beardMain += boxMask(pxUv, heroAnchor + vec2(0.0, 0.095), vec2(0.011, 0.013), 0.004);
                float beardHighlight = boxMask(pxUv, heroAnchor + vec2(0.0, 0.119), vec2(0.014, 0.006), 0.003);
                float mustache = boxMask(pxUv, heroAnchor + vec2(-0.012, 0.132), vec2(0.009, 0.004), 0.002)
                    + boxMask(pxUv, heroAnchor + vec2(0.012, 0.132), vec2(0.009, 0.004), 0.002);
                float hairLeft = boxMask(pxUv, heroAnchor + vec2(-0.026, 0.162), vec2(0.008, 0.011), 0.003);
                float hairRight = boxMask(pxUv, heroAnchor + vec2(0.026, 0.162), vec2(0.008, 0.011), 0.003);

                float helmet = boxMask(pxUv, heroAnchor + vec2(0.0, 0.178), vec2(0.034, 0.014), 0.004);
                float noseGuard = boxMask(pxUv, heroAnchor + vec2(0.0, 0.161), vec2(0.004, 0.014), 0.002);
                float helmetHighlight = boxMask(pxUv, heroAnchor + vec2(0.0, 0.185), vec2(0.025, 0.004), 0.002);
                float helmetRivetL = boxMask(pxUv, heroAnchor + vec2(-0.021, 0.168), vec2(0.002, 0.002), 0.001);
                float helmetRivetR = boxMask(pxUv, heroAnchor + vec2(0.021, 0.168), vec2(0.002, 0.002), 0.001);
                float hornL = ringMask(pxUv, heroAnchor + vec2(-0.038, 0.186), 0.028, 0.01) * step(pxUv.x, heroAnchor.x - 0.01) * step(heroAnchor.y + 0.17, pxUv.y);
                float hornR = ringMask(pxUv, heroAnchor + vec2(0.038, 0.186), 0.028, 0.01) * step(heroAnchor.x + 0.01, pxUv.x) * step(heroAnchor.y + 0.17, pxUv.y);
                float hornTipL = circleMask(pxUv, heroAnchor + vec2(-0.051, 0.205), 0.005, 0.002);
                float hornTipR = circleMask(pxUv, heroAnchor + vec2(0.051, 0.205), 0.005, 0.002);

                float armBackSkin = boxMask(pxUv, heroAnchor + vec2(-0.033, 0.086), vec2(0.011, 0.01), 0.003);
                float armBackForearm = boxMask(pxUv, heroAnchor + vec2(-0.041, 0.061), vec2(0.012, 0.024), 0.004);
                float axeHand = boxMask(pxUv, heroAnchor + vec2(-0.048, 0.04), vec2(0.008, 0.008), 0.002);
                float armFrontSkin = boxMask(pxUv, heroAnchor + vec2(0.03, 0.086), vec2(0.011, 0.01), 0.003);
                float armFrontForearm = boxMask(pxUv, heroAnchor + vec2(0.04, 0.06), vec2(0.013, 0.026), 0.004);
                float shieldHand = boxMask(pxUv, heroAnchor + vec2(0.046, 0.04), vec2(0.008, 0.008), 0.002);

                float axeHandle = segmentGlow(pxUv, heroAnchor + vec2(-0.046, 0.077), heroAnchor + vec2(-0.082, 0.204), 0.01);
                float axeHead = boxMask(pxUv, heroAnchor + vec2(-0.091, 0.214), vec2(0.014, 0.016), 0.004);
                float axeBlade = boxMask(pxUv, heroAnchor + vec2(-0.106, 0.214), vec2(0.012, 0.022), 0.004);
                float axeEdge = boxMask(pxUv, heroAnchor + vec2(-0.112, 0.214), vec2(0.004, 0.018), 0.002);

                col = mix(col, vec3(0.4, 0.3, 0.2), leftBootFur + rightBootFur);
                col = mix(col, vec3(0.23, 0.16, 0.1), leftBoot + rightBoot);
                col = mix(col, vec3(0.29, 0.22, 0.16), leftLeg + rightLeg);
                col = mix(col, vec3(0.41, 0.29, 0.2), torsoBase);
                col = mix(col, vec3(0.48, 0.35, 0.26), vest);
                col = mix(col, vec3(0.56, 0.49, 0.42), furTrim);
                col = mix(col, vec3(0.22, 0.15, 0.09), belt);
                col = mix(col, vec3(0.83, 0.63, 0.19), buckle);

                col = mix(col, vec3(0.87, 0.72, 0.54), armBackSkin + axeHand + head);
                col = mix(col, vec3(0.8, 0.66, 0.49), faceShadeL + faceShadeR + nose);
                col = mix(col, vec3(0.91, 0.79, 0.62), cheek);
                col = mix(col, vec3(0.23, 0.17, 0.1), brow);
                col = mix(col, vec3(0.12, 0.12, 0.16), eyeA + eyeB);
                col = mix(col, vec3(0.84, 0.9, 0.96), eyeCatchA + eyeCatchB);

                col = mix(col, vec3(0.78, 0.6, 0.26), beardMain + mustache + hairLeft + hairRight);
                col = mix(col, vec3(0.87, 0.69, 0.34), beardHighlight);

                col = mix(col, vec3(0.44, 0.51, 0.59), helmet);
                col = mix(col, vec3(0.56, 0.64, 0.72), helmetHighlight + noseGuard);
                col = mix(col, vec3(0.73, 0.8, 0.86), helmetRivetL + helmetRivetR);
                col = mix(col, vec3(0.78, 0.72, 0.53), hornL + hornR);
                col = mix(col, vec3(0.9, 0.82, 0.58), hornTipL + hornTipR);

                col = mix(col, vec3(0.43, 0.31, 0.22), armBackForearm);
                col = mix(col, vec3(0.38, 0.25, 0.16), axeHandle);
                col = mix(col, vec3(0.67, 0.74, 0.8), axeHead + axeBlade);
                col = mix(col, vec3(0.86, 0.91, 0.95), axeEdge);
                col = mix(col, vec3(0.87, 0.72, 0.54), armFrontSkin + shieldHand);
                col = mix(col, vec3(0.43, 0.31, 0.22), armFrontForearm);
                col = mix(col, vec3(0.29, 0.2, 0.13), shieldStrap);
                col = mix(col, vec3(0.42, 0.55, 0.74), shieldRim);
                col = mix(col, vec3(0.27, 0.41, 0.56), shieldFace);
                col = mix(col, vec3(0.34, 0.47, 0.63), shieldCross);
                col = mix(col, vec3(0.68, 0.75, 0.84), shieldBossOuter);
                col = mix(col, vec3(0.82, 0.88, 0.93), shieldBossInner);

                float embers = 0.0;
                for (int spark = 0; spark < 18; spark++) {
                    float fs = float(spark);
                    vec2 sp = vec2(
                        fract(hash(vec2(fs, 4.0)) + t * (0.015 + fs * 0.0015)),
                        0.18 + fract(hash(vec2(fs, 5.0)) + t * (0.02 + fs * 0.001))
                    );
                    embers += smoothstep(0.012, 0.0, distance(pxUv, sp));
                }
                col += vec3(1.0, 0.54, 0.16) * embers * 0.16;

                float pixelGrid = 0.975 - 0.025 * step(0.5, fract(gl_FragCoord.x * 0.5) + fract(gl_FragCoord.y * 0.5));
                float vignette = 1.0 - smoothstep(0.4, 0.95, length((uv - 0.5) * vec2(1.15, 1.05)));
                col *= pixelGrid;
                col *= 0.72 + vignette * 0.4;

                gl_FragColor = vec4(col, 1.0);
            }
        `;

        function compileShader(src, type) {
            const s = gl.createShader(type);
            gl.shaderSource(s, src);
            gl.compileShader(s);
            if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
                console.error(
                    "WebGL shader compile error:",
                    gl.getShaderInfoLog(s),
                );
                gl.deleteShader(s);
                return null;
            }
            return s;
        }

        const vs = compileShader(VERT, gl.VERTEX_SHADER);
        const fs = compileShader(FRAG, gl.FRAGMENT_SHADER);
        if (!vs || !fs) {
            console.error("WebGL shader failed to compile");
            return noopBackground;
        }

        const prog = gl.createProgram();
        gl.attachShader(prog, vs);
        gl.attachShader(prog, fs);
        gl.linkProgram(prog);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
            console.error(
                "WebGL program link error:",
                gl.getProgramInfoLog(prog),
            );
            gl.deleteProgram(prog);
            gl.deleteShader(vs);
            gl.deleteShader(fs);
            return noopBackground;
        }

        gl.useProgram(prog);

        const buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
            gl.STATIC_DRAW,
        );

        const aPos = gl.getAttribLocation(prog, "aPos");
        gl.enableVertexAttribArray(aPos);
        gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

        const uTime = gl.getUniformLocation(prog, "uTime");
        const uRes = gl.getUniformLocation(prog, "uRes");
        const uMotion = gl.getUniformLocation(prog, "uMotion");

        let rafId = null;
        let active = false;
        let startTime = performance.now();

        function resize() {
            const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
            canvas.width = Math.round(canvas.clientWidth * dpr);
            canvas.height = Math.round(canvas.clientHeight * dpr);
            gl.viewport(0, 0, canvas.width, canvas.height);
        }
        resize();
        window.addEventListener("resize", resize);

        function frame() {
            if (!active) return;
            const t = (performance.now() - startTime) / 1000;
            gl.uniform1f(uTime, t);
            gl.uniform2f(uRes, canvas.width, canvas.height);
            gl.uniform1f(uMotion, prefersReducedMotion ? 0.15 : 1.0);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            rafId = requestAnimationFrame(frame);
        }

        function setActive(nextActive) {
            if (nextActive === active) return;
            active = nextActive;
            if (active) {
                resize();
                startTime = performance.now();
                rafId = requestAnimationFrame(frame);
            } else if (rafId) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
        }

        return {
            setActive,
            destroy() {
                setActive(false);
                window.removeEventListener("resize", resize);
                gl.deleteProgram(prog);
                gl.deleteShader(vs);
                gl.deleteShader(fs);
                gl.deleteBuffer(buf);
            },
        };
    }

    $effect(() => {
        webglBackground?.setActive(shouldShowWebGLBackground());
    });

    $effect(() => {
        if (showFpsMeter) startFpsMeter();
        else stopFpsMeter();
    });

    $effect(() => {
        if (typeof window === "undefined") return;
        gameState;
        showGameOver;
        syncGameplayLayout(4);
    });

    onMount(async () => {
        if (typeof window === "undefined") return;
        document.body.classList.add("warrior-game-page");
        showFpsMeter = readFpsDebugPreference();
        window.__warriorFpsMeter = {
            enable: () => setFpsMeterEnabled(true),
            disable: () => setFpsMeterEnabled(false),
            toggle: () => setFpsMeterEnabled(!showFpsMeter),
        };
        fetchScores().then((s) => {
            titleScores = s;
        });
        crossfadeTo(MENU_IDX); // pre-load retro-ragnarok for intro screen; plays immediately if sound is on

        // Init WebGL background for title screen
        if (glCanvasEl) {
            webglBackground = initWebGLBackground(glCanvasEl) ?? {
                setActive() {},
                destroy() {},
            };
            webglBackground.setActive(shouldShowWebGLBackground());
        }

        if (containerEl && "ResizeObserver" in window) {
            gameplayResizeObserver = new ResizeObserver(() => {
                syncGameplayLayout(2);
            });
            gameplayResizeObserver.observe(containerEl);
        }

        onViewportResize = () => syncGameplayLayout(2);
        window.visualViewport?.addEventListener("resize", onViewportResize);

        // Browser blocks autoplay until a user gesture — retry on first interaction
        const onFirstGesture = () => {
            if (isSoundEnabled() && activeIdx >= 0) {
                const a = musicCache.get(activeIdx);
                if (a?.paused) a.play().catch(() => {});
            }
        };
        document.addEventListener("click", onFirstGesture, { once: true });
        document.addEventListener("keydown", onFirstGesture, { once: true });
        document.addEventListener("touchstart", onFirstGesture, { once: true });

        // Observe [data-sound-toggle] aria-pressed to react to mute/unmute
        const watchToggle = () => {
            const buttons = document.querySelectorAll("[data-sound-toggle]");
            if (!buttons.length) return;
            soundObserver = new MutationObserver(() => syncMusic(gameState));
            buttons.forEach((btn) => {
                soundObserver.observe(btn, {
                    attributes: true,
                    attributeFilter: ["aria-pressed"],
                });
            });
        };
        // Give site scripts a tick to render the toggle
        requestAnimationFrame(watchToggle);

        await loadScript(WARRIOR_GAME_SCRIPT_SRC);

        if (window.initWarriorGame) {
            destroyGame = window.initWarriorGame(canvasEl, {
                onScoreChange: (s) => {
                    score = s;
                },
                onLivesChange: (l) => {
                    lives = l;
                },
                onStateChange: (s) => {
                    gameState = s;
                    syncMusic(s);
                    if (s === "gameover") {
                        showGameOver = false;
                        completedGame = false;
                        playWhirlpoolGameOver(() => {
                            openGameOver();
                        });
                    } else if (s === "victory") {
                        completedGame = true;
                        openGameOver();
                    }
                },
                onSectionChange: (si) => {
                    if (gameState !== "playing") return;
                    crossfadeTo(si);
                    prefetchAudio(si + 1);
                },
            });
        }
        syncGameplayLayout(5);
    });

    onDestroy(() => {
        if (typeof document !== "undefined") {
            document.body.classList.remove("warrior-game-page");
        }
        stopFpsMeter();
        cancelLayoutSync();
        if (destroyGame) destroyGame();
        webglBackground?.destroy();
        soundObserver?.disconnect();
        gameplayResizeObserver?.disconnect();
        if (onViewportResize) {
            window.visualViewport?.removeEventListener(
                "resize",
                onViewportResize,
            );
        }
        if (typeof window !== "undefined") {
            delete window.__warriorFpsMeter;
        }
        cancelMusicFade();
        musicCache.forEach((a) => {
            a.pause();
            a.src = "";
        });
        musicCache.clear();
    });

    function handleSoundClick() {
        const enabled = !isSoundEnabled();
        // Delegate to site script — handles rain/thunder audio, localStorage, and UI
        if (window.__dingSyncSound) {
            window.__dingSyncSound(enabled, { persist: true });
        } else {
            // Fallback: update UI manually if site script isn't ready
            try {
                localStorage.setItem(SOUND_KEY, String(enabled));
            } catch {}
            document.querySelectorAll("[data-sound-toggle]").forEach((btn) => {
                btn.setAttribute("aria-pressed", String(enabled));
                btn.classList.toggle("is-on", enabled);
                btn.classList.toggle("is-off", !enabled);
                const label = btn.querySelector("[data-sound-label]");
                if (label)
                    label.textContent = enabled ? "Sound on" : "Sound off";
            });
        }
        syncMusic(gameState);
    }

    function handleFpsToggle() {
        setFpsMeterEnabled(!showFpsMeter);
    }

    function handleStart() {
        syncGameplayLayout(5);
        window.__warriorGameStart?.();
    }

    function handleRestart() {
        showGameOver = false;
        leaderPhase = "entry";
        playerName = "";
        newEntryIdx = -1;
        syncGameplayLayout(5);
        window.__warriorGameRestart?.();
    }

    function playWhirlpoolGameOver(onComplete) {
        const audio = new Audio("/sounds/whirlpool.mp3");
        audio.volume = 0.7;
        audio.play().catch(() => {});

        const canvas = document.createElement("canvas");
        canvas.style.cssText =
            "position:fixed;inset:0;z-index:99999;pointer-events:all;width:100%;height:100%";
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        document.body.appendChild(canvas);

        const ctx = canvas.getContext("2d");
        const cx = canvas.width / 2;
        const cy = canvas.height / 2;
        const maxR = Math.hypot(cx, cy);
        const DURATION = 1500;
        let start = null;

        function frame(now) {
            if (!start) start = now;
            const t = Math.min((now - start) / DURATION, 1);

            ctx.clearRect(0, 0, canvas.width, canvas.height);

            ctx.fillStyle = `rgba(2, 10, 24, ${Math.min(t * 1.8, 0.94)})`;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            const spin = t * Math.PI * 10;
            const ringCount = 18;
            for (let i = 0; i < ringCount; i++) {
                const fi = i / ringCount;
                const r = maxR * fi * Math.max(1 - t * 0.96, 0.04);
                if (r < 2) continue;
                const fadeOut = Math.max(1 - Math.max(t - 0.65, 0) / 0.35, 0);
                const alpha = (1 - fi) * 0.55 * Math.min(t * 5, 1) * fadeOut;
                ctx.strokeStyle = `hsla(${198 + fi * 28}, 72%, ${50 + fi * 22}%, ${alpha})`;
                ctx.lineWidth = 1 + (1 - fi) * 3.5;
                const startAngle = spin + fi * Math.PI * 0.9;
                ctx.beginPath();
                ctx.arc(cx, cy, r, startAngle, startAngle + Math.PI * 1.5);
                ctx.stroke();
            }

            for (let arm = 0; arm < 3; arm++) {
                const armBase = spin + (arm / 3) * Math.PI * 2;
                const armFade = Math.max(1 - Math.max(t - 0.55, 0) / 0.45, 0);
                ctx.strokeStyle = `rgba(60, 188, 255, ${0.4 * Math.min(t * 3, 1) * armFade})`;
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                let first = true;
                const outerR = maxR * Math.max(1 - t * 0.92, 0.08);
                for (let r = outerR; r > 5; r -= 5) {
                    const angle = armBase + ((maxR - r) / maxR) * Math.PI * 5;
                    const px = cx + Math.cos(angle) * r;
                    const py = cy + Math.sin(angle) * r;
                    if (first) {
                        ctx.moveTo(px, py);
                        first = false;
                    } else ctx.lineTo(px, py);
                }
                ctx.stroke();
            }

            const holeR = t * t * maxR * 0.4;
            if (holeR > 1) {
                const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, holeR);
                grad.addColorStop(0, "rgba(0, 0, 0, 1)");
                grad.addColorStop(0.35, "rgba(0, 6, 18, 0.95)");
                grad.addColorStop(1, "rgba(0, 6, 18, 0)");
                ctx.fillStyle = grad;
                ctx.beginPath();
                ctx.arc(cx, cy, holeR, 0, Math.PI * 2);
                ctx.fill();
            }

            if (t < 0.35) {
                const rippleT = t / 0.35;
                for (let rp = 0; rp < 4; rp++) {
                    const rt = Math.max(0, rippleT - rp * 0.08);
                    if (rt <= 0) continue;
                    ctx.strokeStyle = `rgba(100, 220, 255, ${(1 - rt) * 0.45})`;
                    ctx.lineWidth = 2;
                    ctx.beginPath();
                    ctx.arc(cx, cy, rt * maxR * 0.55, 0, Math.PI * 2);
                    ctx.stroke();
                }
            }

            if (t > 0.4) {
                const runeAlpha =
                    Math.min((t - 0.4) / 0.3, 1) *
                    Math.max(1 - (t - 0.85) / 0.15, 0);
                if (runeAlpha > 0) {
                    const valknutPath = new Path2D(
                        "M24.96 25.664l2.752 5.056h-21.504l11.52-20.864 3.904-7.168 10.752 19.712h-19.712v1.28h21.888l-12.928-23.68-5.056 9.28-12.544 22.72h25.856l-3.776-6.912-1.152 0.576zM18.88 11.84l-1.152 0.64 4.672 8.448 1.152-0.64-4.672-8.448zM7.488 22.4h-5.312l10.752-19.712 2.432 4.48 1.152-0.64-3.584-6.528-12.928 23.68h7.488v-1.28z",
                    );
                    const size = 20 + t * 30;
                    const scale = size / 35;
                    ctx.save();
                    ctx.globalAlpha = runeAlpha;
                    ctx.translate(cx - (35 * scale) / 2, cy - (32 * scale) / 2);
                    ctx.scale(scale, scale);
                    ctx.fillStyle = "#ffab00";
                    ctx.shadowColor = "#ffab00";
                    ctx.shadowBlur = 20 / scale;
                    ctx.fill(valknutPath);
                    ctx.shadowBlur = 0;
                    ctx.restore();
                }
            }

            if (t < 1) {
                requestAnimationFrame(frame);
            } else {
                onComplete();
                setTimeout(() => canvas.remove(), 600);
            }
        }

        requestAnimationFrame(frame);
    }
</script>

<svelte:head>
    <title>Warrior of the Gods | dinglandia</title>
    <meta
        name="description"
        content="Guide Ragnar the Viking through the Nine Realms to reach Valhalla. A hidden platformer adventure."
    />
    <meta property="og:title" content="Warrior of the Gods | dinglandia" />
    <meta property="og:site_name" content="dinglandia" />
    <meta
        property="og:url"
        content="https://dinglandia.design/warrior-of-the-gods"
    />
    <meta
        property="og:description"
        content="Guide Ragnar the Viking through the Nine Realms to reach Valhalla. A hidden platformer adventure."
    />
    <meta property="og:type" content="website" />
    <meta
        property="og:image"
        content="https://dinglandia.design/img/dinglandia-warrior-of-the-gods--opengraph.png"
    />
    <meta
        property="og:image:alt"
        content="Warrior of the Gods game art featuring Ragnar in a stormy Norse scene"
    />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:site" content="@dinglestein" />
    <meta name="twitter:title" content="Warrior of the Gods | dinglandia" />
    <meta
        name="twitter:description"
        content="Guide Ragnar the Viking through the Nine Realms to reach Valhalla. A hidden platformer adventure."
    />
    <meta
        name="twitter:image"
        content="https://dinglandia.design/img/dinglandia-warrior-of-the-gods--opengraph.png"
    />
    <meta
        name="twitter:image:alt"
        content="Warrior of the Gods game art featuring Ragnar in a stormy Norse scene"
    />
</svelte:head>

<section class="warrior-game" bind:this={containerEl} data-lenis-prevent>
    <!-- WebGL Background (visible on title/gameover screens) -->
    <canvas
        bind:this={glCanvasEl}
        class="warrior-game__gl-bg"
        class:warrior-game__gl-bg--hidden={!(
            gameState === "title" ||
            showGameOver ||
            gameState === "victory"
        )}
        class:warrior-game__gl-bg--visible={gameState === "title" ||
            showGameOver ||
            gameState === "victory"}
        aria-hidden="true"
    ></canvas>

    <canvas
        bind:this={canvasEl}
        class="warrior-game__canvas"
        class:warrior-game__canvas--hidden={gameState === "title" ||
            showGameOver ||
            gameState === "victory"}
        aria-label="Warrior of the Gods platformer game"
    ></canvas>

    {#if showFpsMeter}
        <div class="warrior-game__fps-meter" aria-live="off">
            <span>{fpsStats.fps.toFixed(1)} FPS</span>
            <span>{fpsStats.avgMs.toFixed(1)} ms avg</span>
            <span>{fpsStats.worstMs.toFixed(1)} ms worst</span>
        </div>
    {/if}

    <!-- HUD -->
    {#if gameState === "playing"}
        <div class="warrior-game__hud">
            <div class="warrior-game__hud-score">
                <span class="warrior-game__hud-label">Runes</span>
                <span class="warrior-game__hud-value">{score}</span>
            </div>
            <div class="warrior-game__hud-controls">
                <div class="warrior-game__hud-sound" onclick={handleSoundClick}>
                    <SoundToggle />
                </div>
                <button
                    type="button"
                    class="warrior-game__debug-toggle warrior-game__debug-toggle--hud"
                    aria-pressed={showFpsMeter}
                    onclick={handleFpsToggle}
                >
                    FPS
                </button>
            </div>
            <div class="warrior-game__hud-lives">
                <span class="warrior-game__hud-label">Lives</span>
                <div class="warrior-game__hud-lives-icons">
                    {#each Array(lives) as _, i}
                        <span class="warrior-game__hud-heart" aria-label="life"
                            >&#129683;</span
                        >
                    {/each}
                </div>
            </div>
        </div>
    {/if}

    <!-- Title Screen -->
    {#if gameState === "title"}
        <div class="warrior-game__overlay warrior-game__overlay--title">
            <div
                class="warrior-game__overlay-card warrior-game__overlay-card--wide warrior-game__overlay-card--title"
            >
                <!-- Rune decorations -->
                <div class="warrior-game__rune-left" aria-hidden="true">
                    &#5765;
                </div>
                <div class="warrior-game__rune-right" aria-hidden="true">
                    &#5809;
                </div>

                <div class="warrior-game__title-lockup">
                    <span
                        class="warrior-game__title-lightning warrior-game__title-lightning--left"
                        aria-hidden="true"
                    ></span>
                    <span
                        class="warrior-game__title-lightning warrior-game__title-lightning--right"
                        aria-hidden="true"
                    ></span>
                    <span
                        class="warrior-game__title-rune warrior-game__title-rune--left-top"
                        aria-hidden="true">ᚠ</span
                    >
                    <span
                        class="warrior-game__title-rune warrior-game__title-rune--left-bottom"
                        aria-hidden="true">ᚱ</span
                    >
                    <span
                        class="warrior-game__title-rune warrior-game__title-rune--right-top"
                        aria-hidden="true">ᛁ</span
                    >
                    <span
                        class="warrior-game__title-rune warrior-game__title-rune--right-bottom"
                        aria-hidden="true">ᛒ</span
                    >
                    <h1
                        class="warrior-game__title warrior-game__title--metallic"
                    >
                        Warrior of the Gods
                    </h1>
                </div>
                <p class="warrior-game__subtitle warrior-game__subtitle--intro">
                    Guide Ragnar through the Nine Realms to Valhalla
                </p>
                <div class="warrior-game__title-body">
                    <div class="warrior-game__title-scores">
                        <p class="warrior-game__scores-heading">
                            <span class="warrior-game__scores-heading-rune"
                                >&#5792;</span
                            >
                            Halls of Valhalla
                            <span class="warrior-game__scores-heading-rune"
                                >&#5792;</span
                            >
                        </p>
                        {#if titleScores.length > 0}
                            <ol
                                class="warrior-game__leaderboard warrior-game__leaderboard--compact"
                            >
                                {#each titleScores as entry, i}
                                    <li class="warrior-game__leaderboard-row">
                                        <span
                                            class="warrior-game__leaderboard-medal"
                                        >
                                            {#if i === 0}🥇{:else if i === 1}🥈{:else if i === 2}🥉{:else}{i +
                                                    1}{/if}
                                        </span>
                                        <span
                                            class="warrior-game__leaderboard-name"
                                            >{entry.name}</span
                                        >
                                        <span
                                            class="warrior-game__leaderboard-score"
                                            >{entry.score}</span
                                        >
                                        <span
                                            class="warrior-game__leaderboard-completion"
                                            title={entry.completed
                                                ? "Reached Valhalla"
                                                : "Fallen in Battle"}
                                        >
                                            {entry.completed ? "✅" : "💀"}
                                        </span>
                                    </li>
                                {/each}
                            </ol>
                        {:else}
                            <p class="warrior-game__scores-empty">
                                No records yet — be the first!
                            </p>
                        {/if}
                    </div>
                    <div class="warrior-game__controls-info">
                        <div class="warrior-game__controls-heading">
                            Controls
                        </div>
                        <div class="warrior-game__controls-grid">
                            <div class="warrior-game__controls-row">
                                <span class="warrior-game__control-keys">
                                    <kbd>&#8592;</kbd>
                                    <kbd>&#8594;</kbd>
                                </span>
                                <span>Move</span>
                            </div>
                            <div class="warrior-game__controls-row">
                                <span class="warrior-game__control-keys">
                                    <kbd class="warrior-game__kbd--wide"
                                        >Space</kbd
                                    >
                                </span>
                                <span>Jump</span>
                            </div>
                            <div class="warrior-game__controls-row">
                                <span
                                    class="warrior-game__control-keys warrior-game__control-keys--combo"
                                >
                                    <kbd>&#8595;</kbd>
                                    <span class="warrior-game__control-plus"
                                        >+</span
                                    >
                                    <kbd>&#8594;</kbd>
                                    <span class="warrior-game__control-plus"
                                        >+</span
                                    >
                                    <kbd class="warrior-game__kbd--wide"
                                        >Space</kbd
                                    >
                                </span>
                                <span>Long Jump</span>
                            </div>
                            <div class="warrior-game__controls-row">
                                <span class="warrior-game__control-keys">
                                    <kbd>&#8595;</kbd>
                                </span>
                                <span>Pound (air)</span>
                            </div>
                            <div class="warrior-game__controls-row">
                                <span class="warrior-game__control-keys">
                                    <kbd>Z</kbd>
                                </span>
                                <span>Throw Axe</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="warrior-game__toggle-row">
                    <div
                        class="warrior-game__sound-row"
                        onclick={handleSoundClick}
                    >
                        <span class="warrior-game__sound-label">Music</span>
                        <SoundToggle />
                    </div>
                    <button
                        type="button"
                        class="warrior-game__debug-toggle"
                        aria-pressed={showFpsMeter}
                        onclick={handleFpsToggle}
                    >
                        FPS
                    </button>
                </div>
                <button
                    type="button"
                    class="warrior-game__btn warrior-game__btn--primary"
                    onclick={handleStart}
                >
                    Begin Journey
                </button>
                <a class="warrior-game__back-link" href="/"
                    >Return to dinglandia.design</a
                >
            </div>
        </div>
    {/if}

    <!-- Game Over -->
    {#if showGameOver}
        <div
            class="warrior-game__overlay {completedGame
                ? 'warrior-game__overlay--victory'
                : 'warrior-game__overlay--gameover'}"
        >
            <div
                class="warrior-game__overlay-card warrior-game__overlay-card--wide"
            >
                {#if scoresLoading}
                    <p class="warrior-game__scores-loading">
                        Consulting the gods…
                    </p>
                {:else if leaderPhase === "entry"}
                    <h2 class="warrior-game__title warrior-game__title--sm">
                        {completedGame
                            ? "Entered Valhalla!"
                            : "Fallen in Battle"}
                    </h2>
                    <p class="warrior-game__subtitle">
                        {score} runes collected — {completedGame
                            ? "the gods welcome you, warrior!"
                            : "your deeds echo through the Nine Realms!"}
                    </p>
                    <form
                        class="warrior-game__name-form"
                        onsubmit={handleNameSubmit}
                    >
                        <label
                            class="warrior-game__name-label"
                            for="warrior-name">Enter your name</label
                        >
                        <input
                            id="warrior-name"
                            class="warrior-game__name-input"
                            type="text"
                            maxlength="20"
                            placeholder="Warrior"
                            bind:value={playerName}
                            autofocus
                        />
                        <button type="submit" class="warrior-game__btn">
                            Claim Your Glory
                        </button>
                    </form>
                    <button
                        type="button"
                        class="warrior-game__skip-link"
                        onclick={handleSkipEntry}
                    >
                        Skip
                    </button>
                {:else if leaderPhase === "board"}
                    <h2 class="warrior-game__title warrior-game__title--sm">
                        {completedGame
                            ? "Entered Valhalla!"
                            : "Fallen in Battle"}
                    </h2>
                    <p class="warrior-game__subtitle">
                        {score} runes collected{!qualifies
                            ? " — not enough to claim glory this time"
                            : ""}
                    </p>

                    {#if scores.length > 0}
                        <div class="warrior-game__top3-wrap">
                            <p class="warrior-game__scores-heading">
                                Top Warriors
                            </p>
                            <ol
                                class="warrior-game__leaderboard warrior-game__leaderboard--top3"
                            >
                                {#each scores.slice(0, 3) as entry, i}
                                    <li
                                        class="warrior-game__leaderboard-row {i ===
                                        newEntryIdx
                                            ? 'warrior-game__leaderboard-row--new'
                                            : ''}"
                                    >
                                        <span
                                            class="warrior-game__leaderboard-medal"
                                        >
                                            {#if i === 0}🥇{:else if i === 1}🥈{:else}🥉{/if}
                                        </span>
                                        <span
                                            class="warrior-game__leaderboard-name"
                                            >{entry.name}</span
                                        >
                                        <span
                                            class="warrior-game__leaderboard-score"
                                            >{entry.score}</span
                                        >
                                        <span
                                            class="warrior-game__leaderboard-completion"
                                            title={entry.completed
                                                ? "Reached Valhalla"
                                                : "Fallen in Battle"}
                                        >
                                            {entry.completed ? "✅" : "💀"}
                                        </span>
                                    </li>
                                {/each}
                            </ol>
                        </div>
                    {/if}

                    <button
                        type="button"
                        class="warrior-game__btn"
                        onclick={handleRestart}
                    >
                        Rise Again
                    </button>
                    <a class="warrior-game__back-link" href="/"
                        >Return to dinglandia.design</a
                    >
                {/if}
            </div>
        </div>
    {/if}

    <!-- Victory -->
    {#if gameState === "victory" && !showGameOver}
        <div class="warrior-game__overlay warrior-game__overlay--victory">
            <div class="warrior-game__overlay-card">
                <h2 class="warrior-game__title warrior-game__title--victory">
                    Valhalla Awaits!
                </h2>
                <p class="warrior-game__subtitle">
                    Score: {score} runes &mdash; The gods welcome you, warrior.
                </p>
                <button
                    type="button"
                    class="warrior-game__btn"
                    onclick={handleRestart}
                >
                    Journey Again
                </button>
                <a class="warrior-game__back-link" href="/"
                    >Return to dinglandia.design</a
                >
            </div>
        </div>
    {/if}

    <!-- Mobile Touch Controls -->
    <div class="warrior-game__touch" aria-hidden="true">
        <div class="warrior-game__dpad">
            <button class="warrior-game__dpad-btn" data-warrior-left
                >&#9664;</button
            >
            <button class="warrior-game__dpad-btn" data-warrior-right
                >&#9654;</button
            >
            <button
                class="warrior-game__dpad-btn warrior-game__dpad-btn--down"
                data-warrior-down>&#9660;</button
            >
        </div>
        <div class="warrior-game__action-btns">
            <button class="warrior-game__jump-btn" data-warrior-jump
                >JUMP</button
            >
            <button class="warrior-game__attack-btn" data-warrior-attack
                >AXE</button
            >
        </div>
    </div>
</section>

<style>
    .warrior-game {
        --warrior-panel-ink: rgba(8, 13, 30, 0.76);
        --warrior-panel-deep: rgba(20, 12, 8, 0.82);
        --warrior-panel-line: rgba(255, 185, 84, 0.34);
        --warrior-panel-frost: rgba(131, 197, 255, 0.12);
        --warrior-panel-ember: rgba(255, 116, 28, 0.18);
        --warrior-copy-soft: #9fb2c6;
        position: fixed;
        inset: 0;
        z-index: 9999;
        background:
            radial-gradient(
                circle at 50% 16%,
                rgba(255, 118, 33, 0.32) 0%,
                transparent 34%
            ),
            radial-gradient(
                circle at 28% 74%,
                rgba(54, 112, 197, 0.15) 0%,
                transparent 32%
            ),
            linear-gradient(180deg, #060914 0%, #0b1222 44%, #05070d 100%);
        overflow: hidden;
    }

    /* WebGL Background Canvas */
    .warrior-game__gl-bg {
        position: absolute;
        inset: 0;
        display: none;
        width: 100%;
        height: 100%;
        z-index: 0;
        opacity: 0;
        transition: opacity 0.8s ease;
        pointer-events: none;
        image-rendering: pixelated;
        filter: saturate(1.12) contrast(1.08) brightness(1.04);
    }

    .warrior-game__gl-bg--hidden {
        display: none;
    }

    .warrior-game__gl-bg--visible {
        display: block;
        opacity: 1;
    }

    .warrior-game__canvas {
        display: block;
        width: 100%;
        height: 100%;
        image-rendering: pixelated;
        image-rendering: crisp-edges;
        position: relative;
        z-index: 1;
        transition: opacity 0.5s ease;
        will-change: transform;
    }

    .warrior-game__canvas--hidden {
        opacity: 0;
        pointer-events: none;
    }

    .warrior-game__fps-meter {
        position: absolute;
        left: 1rem;
        bottom: 1rem;
        z-index: 4;
        display: inline-flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: center;
        padding: 0.45rem 0.7rem;
        border: 1px solid rgba(136, 204, 255, 0.35);
        border-radius: 999px;
        background: rgba(3, 8, 18, 0.78);
        color: #d7f2ff;
        font:
            700 0.74rem/1 "IBM Plex Mono",
            "SFMono-Regular",
            Consolas,
            monospace;
        letter-spacing: 0.04em;
        backdrop-filter: blur(8px);
        pointer-events: none;
        text-transform: uppercase;
    }

    /* HUD */
    .warrior-game__hud {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: center;
        padding: 1rem 1.5rem;
        pointer-events: none;
        font-family: "Skranji", cursive;
        color: #fffff0;
        text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
        z-index: 2;
    }

    .warrior-game__hud-label {
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        opacity: 0.7;
        display: block;
    }

    .warrior-game__hud-value {
        font-size: 1.5rem;
    }

    .warrior-game__hud-lives {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
    }

    .warrior-game__hud-lives-icons {
        display: flex;
        gap: 0.25rem;
    }

    .warrior-game__hud-heart {
        font-size: 1.5rem;
        color: #ffab00;
    }

    .warrior-game__hud-sound {
        display: flex;
        justify-content: center;
        pointer-events: auto;
    }

    .warrior-game__hud-controls {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.65rem;
        pointer-events: auto;
    }

    /* Title screen sound row */
    .warrior-game__sound-row {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.75rem;
    }

    .warrior-game__sound-label {
        font-family: "Lato", sans-serif;
        font-size: 0.85rem;
        color: var(--warrior-copy-soft);
    }

    .warrior-game__toggle-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 0.85rem;
        margin-bottom: 1.5rem;
    }

    .warrior-game__debug-toggle {
        --warrior-toggle-width: clamp(46px, 8vw, 72px);
        border: 1px solid rgba(136, 204, 255, 0.3);
        border-radius: 999px;
        background: rgba(5, 12, 24, 0.74);
        color: #d7f2ff;
        width: var(--warrior-toggle-width);
        aspect-ratio: 8 / 3;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font:
            700 0.72rem/1 "IBM Plex Mono",
            "SFMono-Regular",
            Consolas,
            monospace;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        padding: 0;
        cursor: pointer;
        transition:
            background 0.2s ease,
            border-color 0.2s ease,
            transform 0.2s ease;
    }

    .warrior-game__debug-toggle[aria-pressed="true"] {
        background: rgba(24, 72, 34, 0.72);
        border-color: rgba(125, 225, 160, 0.45);
        color: #e8ffe9;
    }

    .warrior-game__debug-toggle:hover {
        transform: translateY(-1px);
    }

    .warrior-game__debug-toggle--hud {
        width: var(--warrior-toggle-width);
    }

    .warrior-game__debug-toggle:focus-visible {
        outline: 2px solid rgba(136, 204, 255, 0.55);
        outline-offset: 2px;
    }

    /* Overlays */
    .warrior-game__overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(10, 14, 26, 0.85);
        z-index: 10;
    }

    .warrior-game__overlay--title {
        background:
            radial-gradient(
                circle at 50% 18%,
                rgba(255, 125, 39, 0.08) 0%,
                transparent 30%
            ),
            linear-gradient(
                180deg,
                rgba(4, 8, 18, 0.28) 0%,
                rgba(4, 8, 18, 0.08) 28%,
                rgba(4, 8, 18, 0.56) 100%
            );
        backdrop-filter: blur(2px);
    }

    .warrior-game__overlay--victory {
        background: rgba(10, 14, 26, 0.7);
    }

    .warrior-game__overlay--gameover {
        background-color: rgba(28, 2, 2, 0.92);
        background-image:
            radial-gradient(
                ellipse 180px 120px at 8% 12%,
                rgba(160, 0, 0, 0.55) 0%,
                transparent 70%
            ),
            radial-gradient(
                ellipse 120px 160px at 88% 8%,
                rgba(130, 0, 0, 0.45) 0%,
                transparent 70%
            ),
            radial-gradient(
                ellipse 200px 100px at 92% 78%,
                rgba(180, 0, 0, 0.5) 0%,
                transparent 70%
            ),
            radial-gradient(
                ellipse 100px 180px at 6% 88%,
                rgba(120, 0, 0, 0.45) 0%,
                transparent 70%
            ),
            radial-gradient(
                ellipse 160px 120px at 48% 95%,
                rgba(150, 0, 0, 0.4) 0%,
                transparent 70%
            ),
            radial-gradient(
                ellipse 80px 60px at 30% 5%,
                rgba(140, 0, 0, 0.35) 0%,
                transparent 70%
            ),
            radial-gradient(
                ellipse 60px 90px at 70% 50%,
                rgba(100, 0, 0, 0.25) 0%,
                transparent 70%
            );
    }

    .warrior-game__overlay-card {
        text-align: center;
        padding: 2.5rem 3rem;
        max-width: 500px;
    }

    .warrior-game__overlay-card--title {
        position: relative;
        background:
            linear-gradient(
                180deg,
                rgba(255, 162, 64, 0.08) 0%,
                transparent 12%
            ),
            linear-gradient(
                145deg,
                var(--warrior-panel-ink) 0%,
                var(--warrior-panel-deep) 100%
            );
        border: 1px solid var(--warrior-panel-line);
        border-radius: 18px;
        box-shadow:
            0 28px 60px rgba(0, 0, 0, 0.45),
            0 0 0 1px rgba(255, 255, 255, 0.03),
            inset 0 1px 0 rgba(255, 240, 216, 0.1),
            inset 0 -1px 0 rgba(90, 139, 216, 0.18);
        overflow: visible;
        backdrop-filter: blur(8px);

        &::before,
        &::after {
            content: "";
            position: absolute;
            inset: 12px;
            border: 1px solid rgba(255, 210, 140, 0.08);
            border-radius: 12px;
            pointer-events: none;
        }

        &::before {
            box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.02);
        }

        &::after {
            inset: -1px;
            border-radius: 18px;
            border: 1px solid rgba(120, 194, 255, 0.08);
        }
    }

    /* Rune decorations */
    .warrior-game__rune-left,
    .warrior-game__rune-right {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        font-size: 2.5rem;
        color: rgba(255, 171, 0, 0.12);
        font-family: "Skranji", cursive;
        pointer-events: none;
        user-select: none;
    }

    .warrior-game__rune-left {
        left: -2rem;
    }

    .warrior-game__rune-right {
        right: -2rem;
    }

    @media (max-width: 860px) {
        .warrior-game__rune-left,
        .warrior-game__rune-right {
            display: none;
        }

        .warrior-game__title-lightning,
        .warrior-game__title-rune {
            display: none;
        }
    }

    /* ── Title — Metallic 8-bit Style ─────────────── */
    .warrior-game__title {
        font-family: "Skranji", cursive;
        font-size: clamp(2rem, 6vw, 3.5rem);
        color: #ffab00;
        margin: 0 0 0.5rem;
        text-shadow:
            0 0 20px rgba(255, 171, 0, 0.4),
            2px 2px 6px rgba(0, 0, 0, 0.6);
    }

    .warrior-game__title-lockup {
        position: relative;
        margin: 0 0 0.75rem;
        isolation: isolate;
    }

    .warrior-game__title--metallic {
        background-image:
            linear-gradient(
                180deg,
                #ffe066 0%,
                #ffcc00 20%,
                #ffab00 45%,
                #e88800 65%,
                #cc5500 100%
            ),
            repeating-linear-gradient(
                90deg,
                rgba(255, 248, 221, 0.18) 0 4px,
                rgba(255, 248, 221, 0.04) 4px 8px
            ),
            repeating-linear-gradient(
                180deg,
                rgba(255, 255, 255, 0.1) 0 3px,
                rgba(0, 0, 0, 0) 3px 6px
            );
        background-size:
            100% 100%,
            8px 8px,
            100% 6px;
        background-blend-mode: normal, overlay, soft-light;
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        color: transparent;
        text-shadow: none;
        filter: drop-shadow(0 0 12px rgba(255, 171, 0, 0.4))
            drop-shadow(2px 3px 0 #1a0a00) drop-shadow(-1px -1px 0 #1a0a00);
        letter-spacing: 0.02em;
        font-size: clamp(2rem, 6vw, 3.2rem);
        line-height: 1.1;
        margin: 0;
        position: relative;
    }

    .warrior-game__title-lightning {
        position: absolute;
        top: 50%;
        width: 34px;
        height: 96px;
        transform: translateY(-56%);
        opacity: 0.75;
        filter: drop-shadow(0 0 8px rgba(136, 204, 255, 0.6))
            drop-shadow(0 0 16px rgba(136, 204, 255, 0.22));
        pointer-events: none;
    }

    .warrior-game__title-lightning::before {
        content: "";
        position: absolute;
        inset: 0;
        background: #88ccff;
        clip-path: polygon(
            54% 0%,
            78% 22%,
            60% 24%,
            84% 50%,
            65% 52%,
            94% 100%,
            38% 66%,
            52% 64%,
            28% 38%,
            46% 36%
        );
    }

    .warrior-game__title-lightning--left {
        left: 1.8rem;
    }

    .warrior-game__title-lightning--right {
        right: 1.8rem;
        transform: translateY(-56%) scaleX(-1);
    }

    .warrior-game__title-rune {
        position: absolute;
        font-family: "Skranji", cursive;
        font-size: 1.55rem;
        color: rgba(255, 171, 0, 0.76);
        text-shadow:
            0 0 12px rgba(255, 171, 0, 0.26),
            0 0 20px rgba(255, 102, 0, 0.14);
        pointer-events: none;
        user-select: none;
    }

    .warrior-game__title-rune--left-top {
        left: 0.2rem;
        top: 0.1rem;
    }

    .warrior-game__title-rune--left-bottom {
        left: 0.95rem;
        top: 1.75rem;
    }

    .warrior-game__title-rune--right-top {
        right: 0.2rem;
        top: 0.1rem;
    }

    .warrior-game__title-rune--right-bottom {
        right: 0.95rem;
        top: 1.75rem;
    }

    .warrior-game__title--sm {
        font-size: clamp(1.5rem, 5vw, 2.5rem);
    }

    .warrior-game__title--victory {
        color: #ffd700;
        text-shadow:
            0 0 30px rgba(255, 215, 0, 0.6),
            0 0 60px rgba(255, 171, 0, 0.3),
            2px 2px 6px rgba(0, 0, 0, 0.6);
    }

    .warrior-game__subtitle {
        font-family: "Lato", sans-serif;
        font-size: clamp(0.9rem, 2.5vw, 1.15rem);
        color: #c0cede;
        margin: 0 0 1.5rem;
        line-height: 1.5;
    }

    .warrior-game__subtitle--intro {
        color: #a8bdd0;
        font-size: clamp(0.85rem, 2vw, 1rem);
        letter-spacing: 0.04em;
    }

    /* ── Controls Section ───────────────────────── */
    .warrior-game__controls-info {
        font-family: "Lato", sans-serif;
        font-size: 0.78rem;
        color: var(--warrior-copy-soft);
        margin-bottom: 1.5rem;
        height: 100%;
        padding: 1rem 1rem 1.1rem;
        border: 1px solid rgba(117, 178, 255, 0.16);
        border-radius: 14px;
        background:
            linear-gradient(
                180deg,
                rgba(89, 144, 217, 0.14) 0%,
                rgba(7, 12, 28, 0.28) 100%
            ),
            rgba(4, 8, 18, 0.4);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
        backdrop-filter: blur(6px);
        box-sizing: border-box;
    }

    .warrior-game__controls-heading {
        font-family: "Skranji", cursive;
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: #ffab00;
        opacity: 0.6;
        margin: 0 0 0.5rem;
        text-align: center;
    }

    .warrior-game__controls-grid {
        display: flex;
        flex-direction: column;
        gap: 0.46rem;
    }

    .warrior-game__controls-row {
        font-size: clamp(0.78rem, 0.25vw + 0.74rem, 0.92rem);
        line-height: 1.15;
        margin: 0;
        display: grid;
        grid-template-columns: max-content max-content;
        align-items: center;
        justify-content: center;
        justify-items: center;
        gap: 0.5rem;
        text-align: center;
    }

    .warrior-game__controls-row span {
        color: #aabbcc;
        min-width: 0;
    }

    .warrior-game__control-keys {
        display: inline-flex;
        align-items: center;
        gap: 0.22rem;
        min-width: 0;
    }

    .warrior-game__control-keys--combo {
        flex-wrap: wrap;
        row-gap: 0.18rem;
    }

    .warrior-game__control-plus {
        color: #7f93a6;
        font-size: 0.8em;
        line-height: 1;
    }

    .warrior-game__controls-info kbd {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 1.65em;
        background: rgba(255, 171, 0, 0.08);
        border: 1px solid rgba(255, 171, 0, 0.2);
        border-bottom-width: 2px;
        border-radius: 4px;
        padding: 0.1em 0.34em;
        font-family: inherit;
        font-size: 0.78em;
        color: #ccaa66;
        text-shadow: 0 1px 0 rgba(0, 0, 0, 0.3);
        white-space: nowrap;
        flex: 0 0 auto;
    }

    .warrior-game__kbd--wide {
        min-width: 3.55em;
        padding-inline: 0.52em;
    }

    @media (max-width: 560px) {
        .warrior-game__controls-row {
            grid-template-columns: 1fr;
            gap: 0.32rem;
        }
    }

    /* ── Button — 8-bit style ─────────────────── */
    .warrior-game__btn {
        font-family: "Skranji", cursive;
        font-size: 1.1rem;
        padding: 0.75rem 2rem;
        background: linear-gradient(180deg, #ffcc00, #ffab00 40%, #e89000);
        color: #1a0a00;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        transition:
            transform 0.1s,
            filter 0.2s;
        box-shadow:
            0 3px 0 #aa6600,
            0 4px 8px rgba(0, 0, 0, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.3);
        text-shadow: 0 1px 0 rgba(255, 255, 255, 0.2);
        letter-spacing: 0.02em;
        position: relative;
    }

    .warrior-game__btn:hover {
        filter: brightness(1.1);
        transform: translateY(-1px);
        box-shadow:
            0 4px 0 #aa6600,
            0 6px 12px rgba(0, 0, 0, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.3);
    }

    .warrior-game__btn:active {
        transform: translateY(2px);
        box-shadow:
            0 1px 0 #aa6600,
            0 2px 4px rgba(0, 0, 0, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.3);
    }

    .warrior-game__btn--primary {
        font-size: 1.2rem;
        padding: 0.85rem 2.5rem;
        animation: btn-glow 2s ease-in-out infinite;
    }

    @keyframes btn-glow {
        0%,
        100% {
            box-shadow:
                0 3px 0 #aa6600,
                0 4px 8px rgba(0, 0, 0, 0.3),
                0 0 16px rgba(255, 171, 0, 0.15);
        }
        50% {
            box-shadow:
                0 3px 0 #aa6600,
                0 4px 8px rgba(0, 0, 0, 0.3),
                0 0 28px rgba(255, 171, 0, 0.3);
        }
    }

    .warrior-game__back-link {
        display: block;
        margin-top: 1rem;
        font-family: "Lato", sans-serif;
        font-size: 0.85rem;
        color: #556677;
        text-decoration: none;
        transition: color 0.2s;
    }

    .warrior-game__back-link:hover {
        color: #fffff0;
    }

    /* Mobile Touch Controls */
    .warrior-game__touch {
        display: none;
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 1rem 1.5rem 2rem;
        justify-content: space-between;
        align-items: flex-end;
        pointer-events: none;
        z-index: 5;
    }

    @media (hover: none) and (pointer: coarse) {
        .warrior-game__touch {
            display: flex;
        }
    }

    .warrior-game__dpad {
        display: flex;
        gap: 0.5rem;
        pointer-events: auto;
    }

    .warrior-game__dpad-btn,
    .warrior-game__jump-btn {
        pointer-events: auto;
        min-width: 60px;
        min-height: 60px;
        border: 2px solid rgba(255, 255, 240, 0.3);
        border-radius: 12px;
        background: rgba(255, 255, 240, 0.08);
        color: rgba(255, 255, 240, 0.6);
        font-size: 1.5rem;
        font-family: "Skranji", cursive;
        cursor: pointer;
        touch-action: manipulation;
        user-select: none;
        -webkit-user-select: none;
    }

    .warrior-game__dpad-btn:active,
    .warrior-game__jump-btn:active {
        background: rgba(255, 171, 0, 0.25);
        border-color: rgba(255, 171, 0, 0.5);
    }

    .warrior-game__action-btns {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        pointer-events: auto;
    }

    .warrior-game__jump-btn {
        min-width: 80px;
        font-size: 1rem;
        letter-spacing: 0.05em;
    }

    .warrior-game__attack-btn {
        pointer-events: auto;
        min-width: 60px;
        min-height: 54px;
        border: 2px solid rgba(255, 100, 0, 0.3);
        border-radius: 12px;
        background: rgba(255, 100, 0, 0.08);
        color: rgba(255, 140, 40, 0.7);
        font-size: 0.85rem;
        font-family: "Skranji", cursive;
        cursor: pointer;
        touch-action: manipulation;
        user-select: none;
        -webkit-user-select: none;
    }

    .warrior-game__attack-btn:active {
        background: rgba(255, 100, 0, 0.25);
        border-color: rgba(255, 100, 0, 0.6);
        color: rgba(255, 170, 60, 0.9);
    }

    .warrior-game__dpad-btn--down {
        border-color: rgba(255, 221, 68, 0.25);
        color: rgba(255, 221, 68, 0.5);
    }

    .warrior-game__dpad-btn--down:active {
        background: rgba(255, 221, 68, 0.2);
        border-color: rgba(255, 221, 68, 0.6);
    }

    /* ── Leaderboard ─────────────────────────────────── */
    .warrior-game__overlay-card--wide {
        max-width: 780px;
        width: min(780px, 96vw);
        padding: 2rem 2rem 1.75rem;
    }

    /* Two-column layout on the title screen */
    .warrior-game__title-body {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(260px, 1fr);
        align-items: stretch;
        gap: 1.25rem;
        margin-bottom: 1.4rem;
        text-align: left;

        @media (max-width: 480px) {
            grid-template-columns: 1fr;
        }
    }

    .warrior-game__title-scores {
        display: flex;
        flex-direction: column;
        height: 100%;
        padding: 1rem;
        border: 1px solid rgba(255, 177, 77, 0.16);
        border-radius: 14px;
        background:
            linear-gradient(
                180deg,
                rgba(255, 121, 33, 0.14) 0%,
                rgba(16, 9, 7, 0.28) 100%
            ),
            rgba(5, 7, 15, 0.42);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
        backdrop-filter: blur(6px);
        box-sizing: border-box;
    }

    .warrior-game__scores-heading {
        font-family: "Skranji", cursive;
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.14em;
        color: #ffab00;
        opacity: 0.8;
        margin: 0 0 0.5rem;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
    }

    .warrior-game__scores-heading-rune {
        font-size: 0.6rem;
        opacity: 0.5;
    }

    .warrior-game__scores-loading {
        font-family: "Skranji", cursive;
        font-size: 1rem;
        opacity: 0.7;
        text-align: center;
        padding: 2rem 0;
        animation: pulse-opacity 1.2s ease-in-out infinite;
    }

    @keyframes pulse-opacity {
        0%,
        100% {
            opacity: 0.4;
        }
        50% {
            opacity: 0.9;
        }
    }

    .warrior-game__scores-empty {
        font-family: "Lato", sans-serif;
        font-size: 0.8rem;
        color: #7f93a6;
        text-align: center;
        margin: 0.5rem 0;
    }

    .warrior-game__leaderboard--compact .warrior-game__leaderboard-row {
        padding: 0.4rem 0.5rem;
        font-size: 0.78rem;
        gap: 0.3rem;
        grid-template-columns: 1.6rem 1fr auto 1.2rem;
    }

    .warrior-game__leaderboard--top3 {
        max-width: 340px;
        margin-inline: auto;

        .warrior-game__leaderboard-row {
            padding: 0.6rem 1.25rem;
            font-size: 1rem;
        }

        .warrior-game__leaderboard-medal {
            font-size: 1.35rem;
        }

        .warrior-game__leaderboard-name {
            font-size: 1rem;
        }

        .warrior-game__leaderboard-score {
            font-size: 1rem;
        }
    }

    /* Top 3 centering wrapper on death screen */
    .warrior-game__top3-wrap {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        margin-bottom: 1.5rem;
    }

    /* Name entry form */
    .warrior-game__name-form {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 0.75rem;
    }

    .warrior-game__name-label {
        font-family: "Lato", sans-serif;
        font-size: 0.85rem;
        color: #8899aa;
        text-transform: uppercase;
        letter-spacing: 0.08em;
    }

    .warrior-game__name-input {
        font-family: "Skranji", cursive;
        font-size: 1.15rem;
        padding: 0.6rem 1.25rem;
        width: 100%;
        max-width: 280px;
        background: rgba(255, 255, 255, 0.06);
        border: 1px solid rgba(255, 171, 0, 0.35);
        border-bottom-width: 2px;
        border-radius: 6px;
        color: #fffff0;
        text-align: center;
        outline: none;
        transition:
            border-color 0.2s,
            box-shadow 0.2s;

        &:focus {
            border-color: rgba(255, 171, 0, 0.8);
            box-shadow: 0 0 12px rgba(255, 171, 0, 0.15);
        }
    }

    .warrior-game__skip-link {
        background: none;
        border: none;
        font-family: "Lato", sans-serif;
        font-size: 0.8rem;
        color: #556677;
        cursor: pointer;
        padding: 0.25rem 0.5rem;
        transition: color 0.2s;

        &:hover {
            color: #8899aa;
        }
    }

    /* Leaderboard list */
    .warrior-game__leaderboard {
        list-style: none;
        margin: 0 0 1.5rem;
        padding: 0;
        width: 100%;
        border: 1px solid rgba(255, 185, 84, 0.18);
        border-radius: 12px;
        overflow: hidden;
        background: linear-gradient(
            180deg,
            rgba(4, 10, 24, 0.78) 0%,
            rgba(10, 8, 12, 0.78) 100%
        );
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
    }

    .warrior-game__leaderboard-row {
        display: grid;
        grid-template-columns: 2.5rem 1fr auto auto 1.5rem;
        align-items: center;
        gap: 0.5rem;
        padding: 0.55rem 1rem;
        font-family: "Lato", sans-serif;
        font-size: 0.9rem;
        color: #b0bec5;
        border-bottom: 1px solid rgba(255, 255, 255, 0.04);
        transition: background 0.15s;

        &:last-child {
            border-bottom: none;
        }
        &:nth-child(odd) {
            background: rgba(255, 255, 255, 0.02);
        }

        &--new {
            background: rgba(255, 171, 0, 0.1) !important;
            color: #fffff0;
            box-shadow: inset 2px 0 0 #ffab00;
            animation: warrior-row-pulse 1.2s ease-out;
        }
    }

    @keyframes warrior-row-pulse {
        0% {
            background: rgba(255, 171, 0, 0.3);
        }
        100% {
            background: rgba(255, 171, 0, 0.1);
        }
    }

    .warrior-game__leaderboard-medal {
        font-size: 1.1rem;
        text-align: center;
        font-variant-numeric: tabular-nums;
        color: #556677;

        .warrior-game__leaderboard-row:nth-child(-n + 3) & {
            font-size: 1.2rem;
            color: inherit;
        }
    }

    .warrior-game__leaderboard-name {
        font-family: "Skranji", cursive;
        font-size: 0.95rem;
        color: #cdd4db;
        text-align: left;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
    }

    .warrior-game__leaderboard-score {
        font-variant-numeric: tabular-nums;
        color: #ffab00;
        font-weight: 700;
        font-size: 0.95rem;
        white-space: nowrap;
    }

    .warrior-game__leaderboard-date {
        font-size: 0.72rem;
        color: #445566;
        white-space: nowrap;
        padding-left: 0.25rem;
    }

    .warrior-game__leaderboard-completion {
        font-size: 0.9rem;
        text-align: center;
        line-height: 1;
        display: flex;
        align-items: center;
        justify-content: center;
    }
</style>
